import { Injectable } from '@angular/core';
import { NavigationEnd, Route as ngRoute, Router } from '@angular/router';
import { Route } from '@bca-shared/models/_enums';
import * as appActions from '@bca-store/app.actions';
import { IRootState } from '@bca-store/store.reducers';
import { Store, select } from '@ngrx/store';
import { from } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import { createAction } from '@bca-store/models/action-creator';

@Injectable({
  providedIn: 'root'
})
export class RouterService {
  private routes: ngRoute[];

  constructor(private _router: Router, private _store$: Store<IRootState>) {
    this.routes = this._router.config
      .filter(route => route.data)
      .sort((a, b) => (+a.data['routeIndex'] > +b.data['routeIndex'] ? 1 : -1));

    this._store$
      .pipe(
        select(({ app }) => app.currentRoute),
        take(1)
      )
      .subscribe(route => this.navigateTo(route, false));

    this._router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        const { url } = event;
        const path = url.replace('/', '');
        const route = this.routes.findIndex(route => route.path === path) as Route;

        this._store$.dispatch(createAction(new appActions.SetRoute({ route }), RouterService));
      });
  }

  public nextRoute(scrollToTop: boolean = true): Promise<boolean> {
    const path = this._router.url === '/' ? '' : this._router.url.replace('/', '');

    const nextPageIndex = this.routes.findIndex(route => route.path === path) + 1;

    return this._store$
      .pipe(
        select(({ app }) => app.highestRoute),
        take(1),
        map(highestRoute => {
          const highestRouteIndex = this.routes.findIndex(
            route => +route.data['routeIndex'] === +highestRoute
          );
          const { path: nextPath } = this.routes[
            highestRouteIndex > nextPageIndex ? highestRouteIndex : nextPageIndex
          ];
          return nextPath;
        }),
        switchMap(nextPath => from(this._router.navigate([nextPath]))),
        tap(navigated => {
          if (navigated && scrollToTop) {
            window.scrollTo(0, 0);
          }
        })
      )
      .toPromise();
  }

  public async previousRoute(scrollToTop: boolean = true): Promise<boolean> {
    const path = this._router.url === '/' ? '' : this._router.url.replace('/', '');

    const previousPageIndex = this.routes.findIndex(route => route.path === path) - 1;

    const { path: previousPath } = this.routes[previousPageIndex < 0 ? 0 : previousPageIndex];

    const navigated = await this._router.navigate([previousPath]);

    if (navigated && scrollToTop) {
      window.scrollTo(0, 0);
    }

    return navigated;
  }

  public async navigateTo(route: Route, scrollToTop: boolean = true): Promise<boolean> {
    if (route > Route.CONFIRMATION) {
      return false;
    }

    const routeIndex = this.routes.findIndex(r => +r.data['routeIndex'] === +route);

    if (routeIndex === -1) {
      // skip
      return;
    }
    const navigated = await this._router.navigate([this.routes[routeIndex].path]);
    if (navigated && scrollToTop) {
      window.scrollTo(0, 0);
    }
    return navigated;
  }

  public redirectToUrl(url: string, timeoutMs: number = 250): void {
    setTimeout(() => (window.location.href = url), timeoutMs);
  }

  public refreshPage(force: boolean = false, timeoutMs: number = 100): void {
    setTimeout(() => (window.location.reload as (force: boolean) => void)(force), timeoutMs);
  }
}
